import { useCallback, useEffect, useLayoutEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import matchList from '@gin-betting/match-list';
import core, { GQL, Poller } from '@gin-betting/core';

import ui from 'modules/ui';
import type { State } from 'types';
import { checkIfNewsPage, getVariablesForLiveSportEvent } from 'utils';
import { useDidUpdate, usePrevious } from 'hooks';

const {
    utils: {
        getBettingOptions,
        getDefaultVariables,
        getAppliedFilters,
        getSportTypeFromLocation,
    },
    viewport: {
        selectors: { selectIsCompact },
    },
} = core;

const {
    selectors: { selectIsFilterOpened, selectIsMobileTopMatchesOpened },
} = ui;

interface Input {
    poller: Poller.Poller<typeof matchList.actions.searchMatches>;
}

interface Output {
    isBettingPage: boolean;
    isCompact: boolean;
    shouldRenderMatchList: boolean;
}

const excludeFromSearchRegexp = /\??page=\d+/g;

const useMatchListContainer = ({ poller }: Input): Output => {
    const propsToSelect = createStructuredSelector({
        isCompact: selectIsCompact,
        isCategorizerOpened: selectIsFilterOpened,
        isMobileTopMatchesOpened: selectIsMobileTopMatchesOpened,
    });

    const { isCompact, isCategorizerOpened } = useSelector((s: State) =>
        propsToSelect(s, {})
    );

    const location = useLocation();

    const prevLocation = usePrevious(location);

    const configurePoller = useCallback(() => {
        const pollerVariables = getPollerVariables();

        poller.setVariables(pollerVariables);
    }, [poller]);

    const makePollerRequest = useCallback(() => {
        poller.requestContent({
            isLoadMore: false,
            isPolling: false,
            isRepeat: false,
        });
    }, [poller]);

    // withPolling execute executableAction after did mount, but useEffect fires synchronously after all DOM mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => configurePoller(), []);
    const sportType = getSportTypeFromLocation();
    const prevSportType = usePrevious(sportType);
    useDidUpdate(() => {
        if (prevSportType && prevSportType !== sportType) {
            configurePoller();
            makePollerRequest();
        }
    }, [configurePoller, makePollerRequest, prevSportType, sportType]);

    useEffect(() => {
        if (checkPollerNeedsConfigurationUpdate(location, prevLocation)) {
            configurePoller();
            makePollerRequest();
        }
        // prevLocation is returned from usePrevious hook, that uses refs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, configurePoller, makePollerRequest]);

    return {
        isCompact,
        isBettingPage: getBettingOptions().url.pagePath === '',
        shouldRenderMatchList: !isCompact || !isCategorizerOpened,
    };
};

function checkPollerNeedsConfigurationUpdate(
    location: RouteComponentProps['location'],
    prevLocation: RouteComponentProps['location'] | undefined
): boolean {
    if (!prevLocation) return false;

    const { search } = location;
    const { search: prevSearch } = prevLocation;

    return (
        prevSearch.replace(excludeFromSearchRegexp, '') !==
        search.replace(excludeFromSearchRegexp, '')
    );
}

function getPollerVariables(): Partial<GQL.GetMatchesByFiltersQueryVariables> {
    const { sportId, tournamentId } = getAppliedFilters();
    const { selectedSportIds } = getBettingOptions();

    const isNewsPage = checkIfNewsPage();

    const {
        marketStatuses,
        matchStatuses,
        ...restVariables
    } = getDefaultVariables();

    const {
        marketStatusesForLiveSportEvent,
        dateFrom,
    } = getVariablesForLiveSportEvent(matchStatuses, marketStatuses);

    return {
        dateFrom,
        matchStatuses,
        marketStatuses,
        ...restVariables,
        ...(tournamentId && { tournamentIds: [tournamentId] }),
        ...(sportId && { sportIds: [sportId] }),
        ...(isNewsPage &&
            selectedSportIds.length && { sportIds: selectedSportIds }),
        marketStatusesForSportEvent: marketStatusesForLiveSportEvent,
        marketLimit: 3,
        isTopMarkets: true,
    };
}

export default useMatchListContainer;
